import { useRef, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { DownloadSimple, UploadSimple, FileXls, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { usePerms } from "../lib/perms";

const IMPORT_CAP = { customers: "sales.edit", vendors: "vendors.edit", items: "items.edit", projects: "projects.edit" };

function cellValue(c, r) {
  const v = typeof c.value === "function" ? c.value(r) : r[c.key];
  return v === null || v === undefined ? "" : v;
}

function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(cellValue(c, r))).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadXlsx(filename, columns, rows) {
  const aoa = [columns.map((c) => c.label)];
  rows.forEach((r) => aoa.push(columns.map((c) => cellValue(c, r))));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Export");
  XLSX.writeFile(wb, filename);
}

/**
 * CSV/Excel import + export toolbar.
 * props: entity (import endpoint key), columns [{key,label,value?,importKey?}], rows (export data),
 *        queryKey (to invalidate after import), label, importColumns, exportOnly
 */
export function CsvTools({ entity, columns, rows = [], queryKey, label = "records", importColumns, exportOnly: exportOnlyProp = false }) {
  const { can } = usePerms();
  const exportOnly = exportOnlyProp || !can(IMPORT_CAP[entity] || "admin");
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, valid, invalid, total, source }
  const impCols = importColumns || columns.filter((c) => c.importKey !== false).map((c) => c.importKey || c.key);
  const stamp = new Date().toISOString().slice(0, 10);

  const exportCsv = () => {
    if (!rows.length) { toast.info("Nothing to export yet"); return; }
    downloadCsv(`${entity}-${stamp}.csv`, columns, rows);
    toast.success(`Exported ${rows.length} ${label}`);
  };

  const exportExcel = () => {
    if (!rows.length) { toast.info("Nothing to export yet"); return; }
    downloadXlsx(`${entity}-${stamp}.xlsx`, columns, rows);
    toast.success(`Exported ${rows.length} ${label} to Excel`);
  };

  const template = () => {
    const blob = new Blob([impCols.join(",") + "\n"], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${entity}-template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: async (res) => {
        const data = (res.data || []).filter((r) => Object.values(r).some((v) => String(v || "").trim()));
        if (fileRef.current) fileRef.current.value = "";
        if (!data.length) { toast.error("No rows found in file"); return; }
        setBusy(true);
        try {
          const { data: out } = await api.post(`/bulk-import/${entity}?preview=true`, { rows: data });
          setPreview({ ...out, source: data });
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read file");
        } finally {
          setBusy(false);
        }
      },
      error: () => { toast.error("Could not read CSV file"); if (fileRef.current) fileRef.current.value = ""; },
    });
  };

  const commitImport = async () => {
    const validRows = preview.rows.filter((r) => r.valid).map((r) => r.data);
    if (!validRows.length) { toast.error("No valid rows to import"); return; }
    setBusy(true);
    try {
      const { data: out } = await api.post(`/bulk-import/${entity}`, { rows: validRows });
      if (queryKey) qc.invalidateQueries({ queryKey });
      if (out.failed) toast.warning(`Imported ${out.created}, ${out.failed} failed`);
      else toast.success(`Imported ${out.created} ${label}`);
      setPreview(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const previewCols = preview ? Array.from(new Set(preview.rows.flatMap((r) => Object.keys(r.data)))).slice(0, 6) : [];

  return (
    <div className="flex items-center gap-2">
      <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid={`csv-import-input-${entity}`} />
      {!exportOnly && <Button variant="outline" onClick={template} className="rounded-sm h-9 hidden sm:inline-flex" data-testid={`csv-template-${entity}`} title="Download a blank CSV template">Template</Button>}
      {!exportOnly && (
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-sm h-9" data-testid={`csv-import-${entity}`}>
          <UploadSimple size={16} className="mr-1.5" /> {busy && !preview ? "Reading…" : "Import"}
        </Button>
      )}
      <Button variant="outline" onClick={exportCsv} className="rounded-sm h-9" data-testid={`csv-export-${entity}`}>
        <DownloadSimple size={16} className="mr-1.5" /> CSV
      </Button>
      <Button variant="outline" onClick={exportExcel} className="rounded-sm h-9" data-testid={`excel-export-${entity}`}>
        <FileXls size={16} className="mr-1.5" /> Excel
      </Button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl" data-testid={`csv-preview-dialog-${entity}`}>
          <DialogHeader>
            <DialogTitle>Import preview — {label}</DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <div className="flex items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-green-600 dark:text-green-400" data-testid="csv-preview-valid-count">
                  <CheckCircle size={16} weight="fill" /> {preview.valid} valid
                </span>
                <span className="inline-flex items-center gap-1.5 text-destructive" data-testid="csv-preview-invalid-count">
                  <WarningCircle size={16} weight="fill" /> {preview.invalid} invalid
                </span>
                <span className="text-muted-foreground">of {preview.total} rows</span>
              </div>
              <div className="max-h-[50vh] overflow-auto border border-border rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium">Row</th>
                      <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      {previewCols.map((c) => <th key={c} className="text-left px-2 py-1.5 font-medium">{c}</th>)}
                      <th className="text-left px-2 py-1.5 font-medium">Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.row} className={`border-t border-border ${r.valid ? "" : "bg-destructive/5"}`} data-testid={`csv-preview-row-${r.row}`}>
                        <td className="px-2 py-1.5 text-muted-foreground">{r.row}</td>
                        <td className="px-2 py-1.5">
                          {r.valid
                            ? <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle size={14} weight="fill" /> OK</span>
                            : <span className="inline-flex items-center gap-1 text-destructive"><WarningCircle size={14} weight="fill" /> Error</span>}
                        </td>
                        {previewCols.map((c) => <td key={c} className="px-2 py-1.5 max-w-[140px] truncate" title={String(r.data[c] ?? "")}>{String(r.data[c] ?? "")}</td>)}
                        <td className="px-2 py-1.5 text-destructive max-w-[220px] truncate" title={r.error || ""}>{r.error || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">Only valid rows will be imported. Invalid rows are skipped — fix them in your file and re-import.</p>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} className="rounded-sm" data-testid="csv-preview-cancel">Cancel</Button>
            <Button onClick={commitImport} disabled={busy || !preview?.valid} className="rounded-sm" data-testid="csv-preview-confirm">
              {busy ? "Importing…" : `Import ${preview?.valid || 0} valid`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
